(function () {
  function getControlElement(input) {
    return input.closest('.size-card') || input.nextElementSibling;
  }

  function isUnavailable(input) {
    const control = getControlElement(input);
    return control?.classList.contains('is-disabled');
  }

  function parseVariantIds(input) {
    return (input?.dataset.variants || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  function firstAvailableInput(fieldset) {
    if (!fieldset) return null;
    return [...fieldset.querySelectorAll('input[data-option-position]')].find((input) => !isUnavailable(input)) || null;
  }

  function selectInput(input) {
    if (!input || input.checked) return false;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function findSizeInputByName(picker, sizeName) {
    if (!sizeName) return null;

    const sizeFieldset = picker.querySelector('.variant-picker-v2__option--size');
    return (
      [...(sizeFieldset?.querySelectorAll('input[data-option-position]') || [])].find(
        (input) => input.dataset.name === sizeName && !isUnavailable(input)
      ) || null
    );
  }

  function findFlavorForSize(picker, clickedSizeInput) {
    const flavorFieldset = picker.querySelector('.variant-picker-v2__option--flavor');
    if (!flavorFieldset || !clickedSizeInput) return null;

    const sizeVariantIds = parseVariantIds(clickedSizeInput);
    const flavorInputs = [...flavorFieldset.querySelectorAll('input[data-option-position]')];

    const sharedVariantMatch = flavorInputs.find((flavorInput) => {
      if (isUnavailable(flavorInput)) return false;
      const flavorVariantIds = parseVariantIds(flavorInput);
      return sizeVariantIds.some((id) => flavorVariantIds.includes(id));
    });

    if (sharedVariantMatch) return sharedVariantMatch;

    const fallbacksScript = picker.querySelector('script[data-fallbacks]');
    const sizeName = clickedSizeInput.dataset.name;

    if (!fallbacksScript || !sizeName) return null;

    try {
      const fallbacks = JSON.parse(fallbacksScript.textContent || '{}');
      const fallbackVariantId = String(fallbacks[sizeName] ?? '');

      if (!fallbackVariantId || fallbackVariantId === 'null') return null;

      return (
        flavorInputs.find((flavorInput) => {
          if (isUnavailable(flavorInput)) return false;
          return parseVariantIds(flavorInput).includes(fallbackVariantId);
        }) || null
      );
    } catch {
      return null;
    }
  }

  function selectFirstAvailableSize(picker) {
    const sizeFieldset = picker.querySelector('.variant-picker-v2__option--size');
    const firstSize = firstAvailableInput(sizeFieldset);
    const checkedSize = sizeFieldset?.querySelector('input[data-option-position]:checked');

    if (firstSize && firstSize !== checkedSize) {
      selectInput(firstSize);
    }
  }

  function preserveOrSelectSize(picker, preferredSizeName) {
    const sizeFieldset = picker.querySelector('.variant-picker-v2__option--size');
    if (!sizeFieldset) return;

    const checkedSize = sizeFieldset.querySelector('input[data-option-position]:checked');
    const sizeName = preferredSizeName || checkedSize?.dataset.name;
    const preferredSize = findSizeInputByName(picker, sizeName);

    if (preferredSize) {
      if (preferredSize !== checkedSize) {
        selectInput(preferredSize);
      }
      return;
    }

    if (checkedSize && !isUnavailable(checkedSize)) return;

    selectFirstAvailableSize(picker);
  }

  function handleUnavailableSizeClick(picker, clickedSizeInput) {
    const matchingFlavor = findFlavorForSize(picker, clickedSizeInput);

    if (matchingFlavor) {
      if (clickedSizeInput.dataset.name) {
        picker.dataset.preserveSizeName = clickedSizeInput.dataset.name;
      }

      // Flavor already matches (e.g. theme.js fallback clicked this size after a
      // flavor change). Do not "fix" the size — that was undoing 9CT → 3CT
      // and inverting Best Sellers / Full Spread availability.
      if (matchingFlavor.checked) {
        return;
      }

      selectInput(matchingFlavor);
      return;
    }

    selectFirstAvailableSize(picker);
  }

  /**
   * Before theme fetches section HTML for a flavor change, silently select a
   * size that exists for that flavor and update size-card disabled styles
   * immediately so pills don't flash the previous combo while HTML loads.
   */
  function alignSizeToFlavor(picker, flavorInput) {
    const sizeFieldset = picker.querySelector('.variant-picker-v2__option--size');
    if (!sizeFieldset || !flavorInput) return;

    const flavorVariantIds = parseVariantIds(flavorInput);
    if (!flavorVariantIds.length) return;

    const sizeInputs = [...sizeFieldset.querySelectorAll('input[data-option-position]')];
    let compatibleSize = null;

    sizeInputs.forEach((sizeInput) => {
      const sizeVariantIds = parseVariantIds(sizeInput);
      const compatible = sizeVariantIds.some((id) => flavorVariantIds.includes(id));
      const card = sizeInput.closest('.size-card');
      card?.classList.toggle('is-disabled', !compatible);
      if (compatible && !compatibleSize) {
        compatibleSize = sizeInput;
      }
    });

    const checkedSize = sizeFieldset.querySelector('input[data-option-position]:checked');
    const checkedCompatible =
      checkedSize && parseVariantIds(checkedSize).some((id) => flavorVariantIds.includes(id));

    if (compatibleSize && !checkedCompatible) {
      compatibleSize.checked = true;
    }
  }

  // After a flavor change (or any re-render), keep the current size when it is
  // still available; otherwise switch to the first available size.
  function syncVariantPicker(picker) {
    if (!picker) return;
    preserveOrSelectSize(picker);
  }

  function getFormPicker(form) {
    return form?.querySelector('variant-picker.variant-picker-v2') || null;
  }

  function isFormField(form, element) {
    return element instanceof HTMLElement && (form.contains(element) || element.form === form);
  }

  function restoreVariantFocus(form, focusTarget) {
    if (!focusTarget) return;

    const picker = getFormPicker(form);
    const target =
      (focusTarget.id && document.getElementById(focusTarget.id)) ||
      picker?.querySelector(
        `input[data-option-position][name="${CSS.escape(focusTarget.name)}"][value="${CSS.escape(focusTarget.value)}"]`
      );

    if (!target) return;

    target.focus({ preventScroll: true });
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input.matches('input[data-option-position]')) return;

      const picker = input.closest('variant-picker.variant-picker-v2');
      if (!picker) return;

      // Flavor change: align size first so theme does one valid fetch
      if (input.closest('.variant-picker-v2__option--flavor')) {
        alignSizeToFlavor(picker, input);
        return;
      }

      // Unavailable sizes: switch to a flavor that has that size
      if (input.closest('.variant-picker-v2__option--size') && isUnavailable(input)) {
        handleUnavailableSizeClick(picker, input);
      }
    },
    true
  );

  document.addEventListener(
    'product:rerender',
    (event) => {
      const form = event.target;
      if (form?.tagName !== 'FORM') return;

      const picker = getFormPicker(form);
      if (!picker) return;

      const focused = document.activeElement;
      const focusTarget =
        focused instanceof HTMLElement &&
        focused.matches('input[data-option-position]') &&
        isFormField(form, focused)
          ? { id: focused.id, name: focused.name, value: focused.value }
          : null;

      const preferredSizeName = picker.dataset.preserveSizeName || null;
      if (preferredSizeName) {
        delete picker.dataset.preserveSizeName;
      }

      requestAnimationFrame(() => {
        const activePicker = getFormPicker(form);
        if (!activePicker) return;

        // Prefer an explicitly requested size (from unavailable-size click),
        // otherwise keep current size if available, else first available size.
        if (preferredSizeName) {
          preserveOrSelectSize(activePicker, preferredSizeName);
        } else {
          syncVariantPicker(activePicker);
        }

        restoreVariantFocus(form, focusTarget);
      });
    },
    true
  );
})();
